import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { addCancellationPolicy, updateCancellationPolicy, deleteCancellationPolicy } from '../../../redux/Account/ShopDetails/action';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    Divider,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Modal,
    Select,
    Stack,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SuccessMessageModal from '../../Common/SuccessMessage';
import CommonDialog from '../../../common/CommonDialog';
import { MuiSelectWithFocus, MuiTextFieldWithFocus } from '../../../config/MaterialUIConstants';

// Modal style configuration
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

const POLICY_TABLE_STYLES = {
    width: '100%',
    p: '12px',
    border: '1px solid rgba(0, 0, 0, 0.10)',
    borderRadius: '8px',
    boxShadow: '0 2px 3px 0 rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: '#FFF',
};

const TABLE_GRID_STYLES = {
    display: 'grid',
    gridTemplateColumns: `
      1.35fr 12px
      1.35fr 20px
      0.8fr
    `,
    alignItems: 'center',
    width: '100%',
    '@media (max-width: 900px)': {
        gridTemplateColumns: '1fr',
        rowGap: '10px',
    },
};

const TABLE_HEADER_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '38px',
    backgroundColor: '#F8F8F8',
    px: 2,
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F1F1F',
    '@media (max-width: 900px)': {
        display: 'none',
    },
};

const TABLE_ROW_STYLES = {
    ...TABLE_GRID_STYLES,
    width: '100%',
    minHeight: '60px',
    px: 2,
    py: 1.5,
};

/**
 * ShopCancellationPolicy Component
 *
 * This component manages the cancellation policies for a shop.
 * It allows adding, editing, and deleting cancellation policies,
 * with different types (standard/no-show) and configurations.
 *
 * @param {Object} props - Component props
 * @param {Function} props.getShopDetails - Function to refresh shop details after operations
 */
const ShopCancellationPolicy = ({ getShopDetails }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Form management
    const { handleSubmit, reset } = useForm();

    // State for modal controls
    const [open, setOpen] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [openDeleteConformModal, setOpenDeleteConformModal] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);

    // State for success messaging
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // State for policy data
    const [policies, setPolicies] = useState([]);
    const [resultData, setResultData] = useState(null);
    const [policyId, setPolicyId] = useState(null);

    // State for form fields
    const [policyForm, setPolicyForm] = useState({
        selectedValue: '', // cancelUntillType (days/hours)
        typeOfCancel: '', // cancellationType (standard/no_show)
        typeOfAmount: '', // amountType (fixed)
        amountNum: '', // amount
        numOfDays: 0, // cancelUntill
    });

    // Destructure form values for easier access
    const { selectedValue, typeOfCancel, typeOfAmount, amountNum, numOfDays } = policyForm;

    // Get shop details and cancellation policy data from Redux
    const shopDetails = useSelector((state) => state?.account?.shopDetailsData);
    const getPolicyApiData = useSelector((state) => state?.account?.getCancellationPolicy);

    // Update policies when shop details or policy data changes
    useEffect(() => {
        if (shopDetails?.cancellationPolicy) {
            setPolicies(shopDetails.cancellationPolicy);
        }
    }, [shopDetails]);

    useEffect(() => {
        if (getPolicyApiData) {
            setPolicies(getPolicyApiData);
        }
    }, [getPolicyApiData]);

    /**
     * Updates a single field in the policy form
     * @param {string} field - Field name to update
     * @param {any} value - New value for the field
     */
    const updateFormField = (field, value) => {
        setPolicyForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    /**
     * Reset form fields to default values
     */
    const resetForm = () => {
        setPolicyForm({
            selectedValue: '',
            typeOfCancel: '',
            typeOfAmount: '',
            amountNum: '',
            numOfDays: 0,
        });
        setPolicyId(null);
        reset();
    };

    /**
     * Open the policy modal for adding a new policy
     */
    const handleOpenAddModal = () => {
        resetForm();
        setEditModal(false);
        resetForm();
        setOpen(true);
    };

    /**
     * Close the policy modal and reset form
     */
    const handleCloseModal = () => {
        resetForm();
        setOpen(false);
        setEditModal(false);
    };

    /**
     * Open the policy modal for editing an existing policy
     * @param {Object} policy - The policy to edit
     */
    const handleOpenEditModal = (policy) => {
        setPolicyId(policy._id);
        setPolicyForm({
            numOfDays: policy.cancelUntill,
            amountNum: policy.amount,
            selectedValue: policy.cancelUntillType,
            typeOfCancel: policy.cancellationType,
            typeOfAmount: policy.amountType,
        });
        setEditModal(true);
        setOpen(true);
    };

    /**
     * Handle form submission for adding or updating a policy
     */
    const onSubmitPolicy = async () => {
        // Prepare payload with form data
        const payload = {
            amount: amountNum,
            amountType: typeOfAmount,
            cancelUntill: numOfDays,
            cancelUntillType: selectedValue,
            cancellationType: typeOfCancel,
        };

        // Remove fields not applicable for no-show policies
        if (typeOfCancel === 'no_show') {
            delete payload.cancelUntill;
            delete payload.cancelUntillType;
        }

        setShowLoader(true);
        let result;

        try {
            // Update existing policy or add new one based on mode
            if (editModal) {
                result = await dispatch(updateCancellationPolicy(payload, policyId));
            } else {
                result = await dispatch(addCancellationPolicy(payload));
            }

            // If operation succeeded, refresh shop details and close modal
            if (result) {
                getShopDetails();
                handleCloseModal();
            }
        } catch (error) {
            console.error('Error saving policy:', error);
        } finally {
            setShowLoader(false);
        }
    };

    /**
     * Delete a cancellation policy
     */
    const deleteCancellationPolicies = async () => {
        setDeleteLoader(true);

        try {
            await dispatch(deleteCancellationPolicy(policyId));
            getShopDetails();
        } catch (error) {
            console.error('Error during deletion', error);
        } finally {
            setDeleteLoader(false);
            setOpenDeleteConformModal(false);
        }
    };

    /**
     * Prepare to delete a policy
     * @param {string} id - ID of the policy to delete
     */
    const handleDeletePolicy = (id) => {
        setPolicyId(id);
        setOpenDeleteConformModal(true);
    };

    // Validation checks for form fields
    const isFormValid = typeOfCancel && typeOfAmount && amountNum && (typeOfCancel === 'no_show' || (numOfDays && selectedValue));

    return (
        <>
            {/* Main container */}
            <Grid container sx={{ boxShadow: 3, bgcolor: 'white' }} justifyContent={'space-between'} padding={'24px'} borderRadius={'10px'} marginTop={'32px'}>
                {/* Information section */}
                <Grid item xs={12} sm={12} md={4} lg={3} xl={3}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                        Cancellation Policy
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#1F1F1F',
                            lineHeight: '24px',
                            maxWidth: 360,
                        }}
                        component="div"
                    >
                        To minimize no-shows, you have the option to implement a cancellation or a no-show policy for customers. You can set a specific minimum
                        notice period when customers need to cancel to avoid incurring a fee.
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: '12px',
                            fontSize: '16px',
                            color: '#1F1F1F',
                            lineHeight: '24px',
                            maxWidth: 360,
                        }}
                    >
                        While we recommend that customers try to avoid last-minute cancellations and no-shows, implementing a cancellation policy is completely
                        optional. You can continue to use Apex Mechanic without imposing any charges on customers for cancellations or no-shows.
                    </Typography>
                </Grid>

                {/* Policies section */}
                <Grid item xs={12} sm={12} md={8} xl={8} direction={'row'} spacing={2}>
                    <Box sx={POLICY_TABLE_STYLES}>
                        <Box sx={TABLE_HEADER_STYLES}>
                            <Box>Name</Box>
                            <Box />
                            <Box>Amount</Box>
                            <Box />
                            <Box sx={{ justifySelf: 'end' }}>Actions</Box>
                        </Box>

                        {Array.isArray(policies) &&
                            policies.length > 0 &&
                            policies.map((policy) => (
                                <Box sx={TABLE_ROW_STYLES} key={policy?._id}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="body1"
                                            className="label-500"
                                            sx={{
                                                fontSize: '16px',
                                                lineHeight: '160%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {policy?.cancellationType === 'standard' ? 'Standard Policy' : 'No Show'}
                                        </Typography>

                                        {policy?.cancellationType === 'standard' && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: '2px',
                                                    fontSize: '14px',
                                                    lineHeight: '150%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {policy?.cancelUntill} {policy?.cancelUntillType} Until Booking
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box />

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '16px',
                                            color: '#1F1F1F',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        ${Number(policy?.amount || 0).toFixed(2)}
                                    </Typography>

                                    <Box />

                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                        <IconButton
                                            size="small"
                                            aria-label="Edit policy"
                                            onClick={() => handleOpenEditModal(policy)}
                                            sx={{ p: '4px', width: 28, height: 28 }}
                                        >
                                            <Edit sx={{ fontSize: 20, color: '#646466' }} />
                                        </IconButton>

                                        <IconButton
                                            size="small"
                                            aria-label="Delete policy"
                                            onClick={() => handleDeletePolicy(policy?._id)}
                                            sx={{ p: '4px', width: 28, height: 28 }}
                                        >
                                            <Delete sx={{ fontSize: 20, color: '#D32F2F' }} />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            ))}
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={handleOpenAddModal}
                        sx={{
                            width: '100px',
                            height: '38px',
                            padding: '6px 12px',
                            marginTop: '13px',
                            borderRadius: '6px',
                            border: '1px solid #006CDD',
                            background: '#FFF',
                            color: '#006CDD',
                            fontFamily: 'Roboto',
                            fontSize: '15px',
                            fontWeight: 500,
                            lineHeight: '26px',
                            textTransform: 'uppercase',
                            '&:hover': {
                                border: '1px solid #006CDD',
                                background: '#FFF',
                            },
                        }}
                    >
                        ADD
                    </Button>
                </Grid>
            </Grid>

            {/* Add/Edit Policy Modal */}
            {/* Add/Edit Policy Modal */}
            {/* ======================= PIXEL PERFECT MODAL ======================= */}

            <Modal
                open={open}
                onClose={(event, reason) => {
                    if (reason && reason === 'backdropClick') {
                        return;
                    }
                    handleCloseModal();
                }}
                closeAfterTransition
                BackdropProps={{
                    sx: {
                        background: 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(2px)',
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',

                        width: '100%',
                        maxWidth: '560px',

                        bgcolor: '#FFFFFF',

                        borderRadius: '12px',

                        boxShadow: '0px 24px 48px rgba(15, 23, 42, 0.18)',

                        overflow: 'hidden',
                        outline: 'none',

                        '@media (max-width:600px)': {
                            width: '95%',
                            maxHeight: '92vh',
                            overflowY: 'auto',
                        },
                    }}
                >
                    {/* ================= HEADER ================= */}
                    <Box
                        sx={{
                            height: '64px',

                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',

                            px: '24px',

                            background: '#FFFFFF',

                            borderBottom: '1px solid rgba(0,0,0,0.10)!important',
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#111827',

                                fontSize: '20px',
                                fontWeight: 600,
                                lineHeight: '28px',

                                fontFamily: 'Roboto',
                            }}
                        >
                            {editModal ? 'Edit Cancellation Policy' : 'Add Cancellation Policy'}
                        </Typography>

                        <IconButton
                            onClick={handleCloseModal}
                            disableRipple
                            sx={{
                                width: 36,
                                height: 36,

                                borderRadius: '8px',

                                color: '#4B5563',

                                '&:hover': {
                                    background: '#F3F4F6',
                                },
                            }}
                        >
                            <CloseIcon
                                sx={{
                                    fontSize: 22,
                                }}
                            />
                        </IconButton>
                    </Box>

                    {/* ================= BODY ================= */}
                    <Box
                        sx={{
                            px: '24px',
                            pt: '24px',
                            pb: '20px',
                        }}
                    >
                        <form onSubmit={handleSubmit(onSubmitPolicy)}>
                            <Stack spacing={3}>
                                {/* TYPE OF CANCELLATION */}
                                <Box>
                                    <Typography
                                        sx={{
                                            color: '#111827',

                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '20px',
                                            mb: '8px',
                                        }}
                                    >
                                        Type of Cancellation
                                        <Box
                                            component="span"
                                            sx={{
                                                color: '#DC2626',
                                            }}
                                        >
                                            {' '}
                                            *
                                        </Box>
                                    </Typography>

                                    <FormControl fullWidth>
                                        <Select
                                            value={typeOfCancel}
                                            displayEmpty
                                            onChange={(e) => updateFormField('typeOfCancel', e.target.value)}
                                            sx={MuiSelectWithFocus}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        mt: '6px',
                                                        borderRadius: '10px',

                                                        boxShadow: '0px 10px 30px rgba(0,0,0,0.12)',

                                                        '& .MuiMenuItem-root': {
                                                            minHeight: '42px',
                                                            fontSize: '15px',
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                Select cancellation type
                                            </MenuItem>

                                            <MenuItem value="standard">Standard</MenuItem>

                                            <MenuItem value="no_show">No Show</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* CUSTOMER CAN CANCEL */}
                                {typeOfCancel !== 'no_show' && (
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: '#111827',

                                                fontSize: '14px',
                                                fontWeight: 500,
                                                lineHeight: '20px',

                                                mb: '8px',
                                            }}
                                        >
                                            Customer Can Cancel Until
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: '#DC2626',
                                                }}
                                            >
                                                {' '}
                                                *
                                            </Box>
                                        </Typography>

                                        <Grid container spacing={2}>
                                            {/* VALUE */}
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    sx={MuiTextFieldWithFocus}
                                                    value={numOfDays}
                                                    placeholder="Enter value"
                                                    onChange={(e) => {
                                                        const value = e.target.value;

                                                        if (/^\d*$/.test(value)) {
                                                            updateFormField('numOfDays', value);
                                                        }
                                                    }}
                                                />
                                            </Grid>

                                            {/* TYPE */}
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth>
                                                    <Select
                                                        value={selectedValue}
                                                        displayEmpty
                                                        onChange={(e) => updateFormField('selectedValue', e.target.value)}
                                                        sx={MuiSelectWithFocus}
                                                    >
                                                        <MenuItem value="" disabled>
                                                            Select Type
                                                        </MenuItem>

                                                        <MenuItem value="days">Days</MenuItem>

                                                        <MenuItem value="hours">Hours</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* TYPE OF AMOUNT */}
                                <Box>
                                    <Typography
                                        sx={{
                                            color: '#111827',

                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '20px',

                                            mb: '8px',
                                        }}
                                    >
                                        Type of Amount
                                        <Box
                                            component="span"
                                            sx={{
                                                color: '#DC2626',
                                            }}
                                        >
                                            {' '}
                                            *
                                        </Box>
                                    </Typography>

                                    <FormControl fullWidth>
                                        <Select
                                            value={typeOfAmount}
                                            displayEmpty
                                            onChange={(e) => updateFormField('typeOfAmount', e.target.value)}
                                            sx={MuiSelectWithFocus}
                                        >
                                            <MenuItem value="" disabled>
                                                Select amount type
                                            </MenuItem>

                                            <MenuItem value="fixed">Fixed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* AMOUNT */}
                                <Box>
                                    <Typography
                                        sx={{
                                            color: '#111827',

                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '20px',

                                            mb: '8px',
                                        }}
                                    >
                                        Amount
                                        <Box
                                            component="span"
                                            sx={{
                                                color: '#DC2626',
                                            }}
                                        >
                                            {' '}
                                            *
                                        </Box>
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Enter Amount"
                                        value={amountNum}
                                        onChange={(e) => {
                                            const value = e.target.value;

                                            if (/^\d*\.?\d*$/.test(value)) {
                                                updateFormField('amountNum', value);
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <Typography
                                                    sx={{
                                                        color: '#6B7280',
                                                        mr: 1,
                                                        fontSize: '15px',
                                                    }}
                                                >
                                                    $
                                                </Typography>
                                            ),
                                        }}
                                        sx={MuiTextFieldWithFocus}
                                    />
                                </Box>

                                {/* PREVIEW */}
                                {typeOfCancel && amountNum && (
                                    <Box
                                        sx={{
                                            background: '#F9FAFB',

                                            border: '1px solid #E5E7EB',

                                            borderRadius: '10px',

                                            p: '16px',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: '#374151',

                                                fontSize: '14px',

                                                lineHeight: '22px',
                                            }}
                                        >
                                            {typeOfCancel === 'no_show' ? (
                                                <>
                                                    If a customer does not show up, a charge of <strong>${amountNum}</strong> will be applied.
                                                </>
                                            ) : (
                                                <>
                                                    Customers can cancel until{' '}
                                                    <strong>
                                                        {numOfDays} {selectedValue}
                                                    </strong>{' '}
                                                    before appointment. After that, a fee of <strong>${amountNum}</strong> will be charged.
                                                </>
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>

                            {/* ================= FOOTER ================= */}
                            <Divider
                                sx={{
                                    mt: '24px',
                                    width: 'calc(100% + 48px)',
                                    ml: '-24px',
                                    borderColor: 'rgba(0, 0, 0, 0.12)',
                                }}
                            />

                            <Box
                                sx={{
                                    px: '0px',
                                    py: '20px',

                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',

                                    '@media (max-width:600px)': {
                                        flexDirection: 'column-reverse',
                                        gap: 2,
                                    },
                                }}
                            >
                                {/* DELETE */}
                                <Box>
                                    {editModal && (
                                        <Button
                                            variant="text"
                                            color="error"
                                            onClick={() => setOpenDeleteConformModal(true)}
                                            sx={{
                                                height: '40px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </Box>

                                {/* BUTTONS */}
                                <Stack direction="row" spacing={2}>
                                    <Button variant="outlined" color="primary" onClick={handleCloseModal}>
                                        Cancel
                                    </Button>

                                    <Button variant="contained" color="primary" type="submit" disabled={!isFormValid}>
                                        {showLoader ? <CircularProgress size={20} sx={{ color: 'white' }} /> : editModal ? 'Update' : 'Save'}
                                    </Button>
                                </Stack>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <CommonDialog
                loader={deleteLoader}
                open={openDeleteConformModal}
                onClose={() => {
                    if (!deleteLoader) {
                        setOpenDeleteConformModal(false);
                    }
                }}
                onSave={deleteCancellationPolicies}
                dialogTitle="Delete Confirmation"
                dialogSubtitle="Are you sure you want to delete this policy?"
            />

            {/* Loading Dialog */}
            <Dialog fullScreen={false} open={showLoader}>
                <Container sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Container>
            </Dialog>

            {/* Success Message Modal */}
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </>
    );
};

export default ShopCancellationPolicy;
